import { VideoSection } from "@/components/shared/video-section";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  OnboardingMessageType,
  MessageContext,
} from "@/lib/api/onboarding-messages";
import {
  useMessages,
  useCreateMessage,
  useCreateVideoMessage,
  useDeleteMessage,
  useReorderMessages,
} from "@/lib/hooks/onboarding-messages/use-onboarding-messages";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PlusCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { OnboardingMessagesList } from "./onboarding-messages-list";
import { OnboardingMessagesToolbar } from "./onboarding-messages-toolbar";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface OnboardingMessagesSectionProps {
  userId: string;
  showVideo?: boolean;
}

export function OnboardingMessagesSection({
  userId,
  showVideo = true,
}: OnboardingMessagesSectionProps) {
  const [activeTab, setActiveTab] = useState<MessageContext>(
    MessageContext.ONLINE,
  );
  const [newMessage, setNewMessage] = useState("");

  const { data: onlineMessages = [], isLoading: isLoadingOnline } = useMessages(
    userId,
    MessageContext.ONLINE,
  );
  const { data: offlineMessages = [], isLoading: isLoadingOffline } =
    useMessages(userId, MessageContext.OFFLINE);

  const onlineReorderMessages = useReorderMessages(
    userId,
    MessageContext.ONLINE,
  );
  const offlineReorderMessages = useReorderMessages(
    userId,
    MessageContext.OFFLINE,
  );
  const onlineCreateMessage = useCreateMessage(userId, MessageContext.ONLINE);
  const offlineCreateMessage = useCreateMessage(userId, MessageContext.OFFLINE);
  const onlineCreateVideoMessage = useCreateVideoMessage(
    userId,
    MessageContext.ONLINE,
  );
  const offlineCreateVideoMessage = useCreateVideoMessage(
    userId,
    MessageContext.OFFLINE,
  );
  const onlineDeleteMessage = useDeleteMessage(userId, MessageContext.ONLINE);
  const offlineDeleteMessage = useDeleteMessage(userId, MessageContext.OFFLINE);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const messages =
    activeTab === MessageContext.ONLINE ? onlineMessages : offlineMessages;
  const isLoading =
    activeTab === MessageContext.ONLINE ? isLoadingOnline : isLoadingOffline;
  const reorderMessages =
    activeTab === MessageContext.ONLINE
      ? onlineReorderMessages
      : offlineReorderMessages;
  const createMessage =
    activeTab === MessageContext.ONLINE
      ? onlineCreateMessage
      : offlineCreateMessage;
  const createVideoMessage =
    activeTab === MessageContext.ONLINE
      ? onlineCreateVideoMessage
      : offlineCreateVideoMessage;
  const deleteMessage =
    activeTab === MessageContext.ONLINE
      ? onlineDeleteMessage
      : offlineDeleteMessage;

  const fileMessage = useMemo(() => {
    return messages.find(
      (message) => message.type === OnboardingMessageType.FILE,
    );
  }, [messages]);

  const textMessages = useMemo(() => {
    return messages.filter(
      (message) => message.type === OnboardingMessageType.TEXT,
    );
  }, [messages]);

  const { url: fileMessageSignedUrl } = useSignedMedia(fileMessage?.message);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = messages.findIndex((msg) => msg.id === active.id);
    const newIndex = messages.findIndex((msg) => msg.id === over.id);

    const newMessages = arrayMove(messages, oldIndex, newIndex);
    reorderMessages.mutate(newMessages.map((msg) => msg.id));
  };

  const handleAddMessage = () => {
    if (newMessage.trim()) {
      createMessage.mutate({
        message: newMessage,
      });
      setNewMessage("");
    }
  };

  const handleDeleteMessage = (messageId: string) => {
    deleteMessage.mutate(messageId);
  };

  return (
    <div className="space-y-4">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as MessageContext)}
      >
        <TabsList>
          <TabsTrigger value={MessageContext.ONLINE}>
            Online Messages
          </TabsTrigger>
          <TabsTrigger value={MessageContext.OFFLINE}>
            Offline Messages
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        {showVideo ? (
          <div>
            <VideoSection
              title={
                activeTab === MessageContext.OFFLINE
                  ? "Offline Video"
                  : "Onboarding Video"
              }
              videoUrl={fileMessageSignedUrl}
              onUpload={async (file) => {
                await createVideoMessage.mutateAsync({ file });
              }}
              onDelete={async () => {
                if (fileMessage?.id) {
                  await handleDeleteMessage(fileMessage?.id);
                }
              }}
              isUploading={createVideoMessage.isPending}
              maxSize={256}
            />
          </div>
        ) : null}

        <Separator />

        <OnboardingMessagesToolbar
          messagesCount={textMessages.length}
          title={
            activeTab === MessageContext.OFFLINE
              ? "Offline Messages"
              : "Online Messages"
          }
          desc={
            activeTab === MessageContext.OFFLINE
              ? "Add messages that will be sent to teens when mentors are offline"
              : "Add messages that will be sent to teens during onboarding"
          }
        />

        <Card className="p-4">
          <div className="flex flex-col gap-2">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="min-h-[100px]"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleAddMessage}
                disabled={!newMessage.trim() || createMessage.isPending}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Message
              </Button>
            </div>
          </div>
        </Card>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={textMessages.map((msg) => msg.id)}
            strategy={verticalListSortingStrategy}
          >
            <OnboardingMessagesList
              messages={textMessages}
              isLoading={isLoading}
              userId={userId}
              handleDeleteMessage={handleDeleteMessage}
              isOffline={activeTab === MessageContext.OFFLINE}
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
